import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNotificationContext } from "contexts/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import { RouteName } from "constants/route";
import { IDriverUpdateInput } from "models/driver";
import { fetchDriverByIdApi, updateDriverApi } from "api/driver";
import { ILoadingData, ILoadingWithPage } from "models";
import { ICity } from "models/city";
import { useForm } from "react-hook-form";
import { updateDriverValidator } from "validators/createDriver.validator";
import { yupResolver } from "@hookform/resolvers/yup";
import { citiesApi } from "api/city";
import { RcFile } from "antd/es/upload";
import {
  uploadBoloApi,
  uploadBusinessLicenseApi,
  uploadDriverApi,
  uploadDriverIdApi,
  uploadDriverLicenseApi,
  uploadEmploymentAgreementApi,
  uploadInsuranceSubscriptionApi,
  uploadMaheberSupportApi,
  uploadOthersApi,
  uploadOwnerIdApi,
} from "api/upload";

export default function useEditDriver() {
  const id: string = useParams().id!;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const [loading, setLoading] = useState<ILoadingWithPage>({
    button: false,
    page: true,
  });
  const [cities, setCities] = useState<ILoadingData<ICity>>({
    data: [],
    loading: false,
  });
  const { control, handleSubmit, setValue } = useForm<IDriverUpdateInput>({
    resolver: yupResolver(updateDriverValidator),
  });

  useEffect(() => {
    fetchDriverByIdApi(id).then(({ data, error }) => {
      if (data?.data) {
        setValue("first_name", data.data.first_name);
        setValue("middle_name", data.data.middle_name);
        setValue("last_name", data.data.last_name);
        setValue("local_full_name", data.data.local_full_name ?? "");
        setValue("phone", data.data.phone);
        setValue("backup_phone", data.data.backup_phone);
        setValue("city_id", data.data.city_id);
        setValue("email", data.data.email);
        setValue("gender", data.data.gender);
        setValue("tin_number", data.data.tin_number);
        setValue("date_of_birth", dayjs(data.data.date_of_birth) as any);
        setValue("image", data.data.image ? [{ url: data.data.image }] : []);
        setValue(
          "driving_license_image",
          data.data.driving_license_image
            ? [{ url: data.data.driving_license_image }]
            : []
        );
        setValue(
          "insurance_subscription_image",
          data.data.insurance_subscription_image
            ? [{ url: data.data.insurance_subscription_image }]
            : []
        );
        setValue(
          "employment_agreement_image",
          data.data.employment_agreement_image
            ? [{ url: data.data.employment_agreement_image }]
            : []
        );
        setValue(
          "maheber_support_image",
          data.data.maheber_support_image
            ? [{ url: data.data.maheber_support_image }]
            : []
        );
        setValue(
          "driver_id_image",
          data.data.driver_id_image ? [{ url: data.data.driver_id_image }] : []
        );
        setValue(
          "owner_id_image",
          data.data.owner_id_image ? [{ url: data.data.owner_id_image }] : []
        );
        setValue(
          "bolo_image",
          data.data.bolo_image ? [{ url: data.data.bolo_image }] : []
        );
        setValue(
          "business_license_images",
          Array.isArray(data.data.business_license_images)
            ? data.data.business_license_images.map((value) => ({ url: value }))
            : []
        );
        setValue(
          "other_images",
          Array.isArray(data.data.other_images)
            ? data.data.other_images.map((value) => ({ url: value }))
            : []
        );
        setLoading({ button: false, page: false });
      } else showError(error!.message);
    });
    citiesApi(1, 100, "").then(({ data, error }) => {
      if (data) setCities({ data: data.data, loading: false });
      else showError(error!.message);
    });
  }, [id]); // eslint-disable-line

  const onSubmit = async (input: IDriverUpdateInput) => {
    setLoading({ button: true, page: false });
    
    // Helper to extract URL from various formats
    const extractUrl = (val: any) => {
      if (Array.isArray(val) && val.length > 0) {
        const file = val[0];
        return file.url || file.response?.data?.url || file.response?.url || null;
      }
      return null;
    };

    input.image = extractUrl(input.image);
    input.driving_license_image = extractUrl(input.driving_license_image);
    input.insurance_subscription_image = extractUrl(input.insurance_subscription_image);
    input.employment_agreement_image = extractUrl(input.employment_agreement_image);
    input.owner_id_image = extractUrl(input.owner_id_image);
    input.driver_id_image = extractUrl(input.driver_id_image);
    input.maheber_support_image = extractUrl(input.maheber_support_image);
    input.bolo_image = extractUrl(input.bolo_image);

    if (Array.isArray(input.business_license_images) && input.business_license_images.length > 0) {
      input.business_license_images = input.business_license_images.map((file: any) => 
        file.url || file.response?.data?.url || file.response?.url || ""
      ).filter(Boolean);
    } else input.business_license_images = null;

    if (Array.isArray(input.other_images) && input.other_images.length > 0) {
      input.other_images = input.other_images.map((file: any) => 
        file.url || file.response?.data?.url || file.response?.url || ""
      ).filter(Boolean);
    } else input.other_images = null;

    const { data: response, error } = await updateDriverApi(id, input);
    if (response) {
      showSuccess("Driver updated Successfully");
      navigate(RouteName.DRIVER, { replace: true });
    } else {
      showError(error!.message);
    }
    setLoading({ button: false, page: false });
  };

  const handleUploadDriverImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadDriverLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadInsuranceSubscriptionImage = async (file: RcFile) => {
    const { data, error } = await uploadInsuranceSubscriptionApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadEmploymentAgreementImage = async (file: RcFile) => {
    const { data, error } = await uploadEmploymentAgreementApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadMaheberSupportImage = async (file: RcFile) => {
    const { data, error } = await uploadMaheberSupportApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadDriverIdImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverIdApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadOwnerIdImage = async (file: RcFile) => {
    const { data, error } = await uploadOwnerIdApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadBoloImage = async (file: RcFile) => {
    const { data, error } = await uploadBoloApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadBusinessLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadBusinessLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  return {
    loading,
    control,
    cities,
    handleSubmit,
    handleUploadDriverImage,
    handleUploadDriverLicenseImage,
    handleUploadInsuranceSubscriptionImage,
    handleUploadEmploymentAgreementImage,
    handleUploadMaheberSupportImage,
    handleUploadDriverIdImage,
    handleUploadOwnerIdImage,
    handleUploadBoloImage,
    handleUploadBusinessLicenseImage,
    handleUploadOtherImages: async (file: RcFile) => {
      const { data, error } = await uploadOthersApi(file);
      if (data && data.data) return data.data.url;
      else showError(`Error while uploading image: ${error?.message}`);
    },
    onSubmit,
  };
}
